import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { BiztraModal, BiztraModalBody, BiztraModalFooter } from '@this/components/expenses/ui/feedbacks/modal';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Checkbox } from '@this/components/shared/ui/inputs/checkbox';

export type PrintTarget = 'detail' | 'list';

const PRINT_TARGET_OPTIONS: { value: PrintTarget; label: string; description: string }[] = [
  { value: 'detail', label: '明細', description: '経費明細（旅費交通費・その他）を印刷します。' },
  { value: 'list', label: '一覧', description: '精算一覧を印刷します。' }
];

const accentColor = '#1AA3C0';

const checkMarkImage = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M5 10.5l3.2 3.2L15 6.8" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
)}")`;

// 共通 Checkbox の見た目をデザイン（塗りつぶしの角丸チェックボックス、項目名の行に揃える）に合わせて上書きする
const useTargetCheckboxStyle = makeStyles({
  root: {
    alignItems: 'flex-start',
    width: '100%',
    '& .MuiCheckbox-root': {
      marginTop: '1px',
      marginRight: '12px'
    },
    '& .MuiSvgIcon-root': {
      width: '20px',
      height: '20px',
      boxSizing: 'border-box',
      borderRadius: '4px',
      border: '2px solid #C5CED6',
      backgroundColor: '#FFFFFF',
      '& path': {
        display: 'none'
      }
    },
    '& .Mui-checked .MuiSvgIcon-root': {
      borderColor: accentColor,
      backgroundColor: accentColor,
      backgroundImage: checkMarkImage,
      backgroundSize: '100% 100%'
    }
  }
});

type Props = {
  open: boolean;
  onClose: () => void;
  onPrint: (targets: PrintTarget[]) => void;
};

const PrintTargetSelectModal: React.FC<Props> = ({ open, onClose, onPrint }) => {
  const [targets, setTargets] = useState<PrintTarget[]>(['detail', 'list']);
  const checkboxStyles = useTargetCheckboxStyle();

  const handleToggle = (value: PrintTarget, checked: boolean) => {
    setTargets(current => (checked ? [...current, value] : current.filter(target => target !== value)));
  };

  // 明細→一覧の順で出力するため、選択順ではなく選択肢の並び順で渡す
  const handlePrint = () => {
    onPrint(PRINT_TARGET_OPTIONS.map(option => option.value).filter(value => targets.includes(value)));
  };

  return (
    <BiztraModal open={open} onClose={onClose}>
      <BiztraModalBody>
        <h2 style={{ fontSize: '21px', fontWeight: 'bold', margin: '8px 0 12px' }}>印刷する帳票を選択</h2>
        <p style={{ color: '#6B7280', fontSize: '15px', lineHeight: 1.7, marginBottom: '16px' }}>
          出力したい帳票にチェックを入れてください。明細と一覧の両方にチェックすると、明細のあとに一覧が続きます。
        </p>
        {PRINT_TARGET_OPTIONS.map(option => (
          <div
            key={option.value}
            style={{ backgroundColor: '#F0FAFC', borderRadius: '8px', padding: '12px 16px', marginBottom: '8px' }}
          >
            <Checkbox
              className={checkboxStyles.root}
              checked={targets.includes(option.value)}
              onChange={e => handleToggle(option.value, e.target.checked)}
            >
              <span style={{ display: 'block', fontWeight: 'bold', lineHeight: '22px' }}>{option.label}</span>
              <span style={{ display: 'block', color: '#6B7280', fontSize: '0.875rem', marginTop: '4px' }}>
                {option.description}
              </span>
            </Checkbox>
          </div>
        ))}
      </BiztraModalBody>
      <BiztraModalFooter style={{ borderTop: '1px solid #eee', padding: '16px 24px' }}>
        <Button color="sub" onClick={onClose}>
          キャンセル
        </Button>
        <Button disabled={targets.length === 0} onClick={handlePrint}>
          印刷
        </Button>
      </BiztraModalFooter>
    </BiztraModal>
  );
};

export default PrintTargetSelectModal;
